import React from 'react';
import { Accordion, Dropdown } from '@weezevent/nacre';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { StopPropagation } from '../../../../../../components/StopPropagation';
import { DefinitionsList } from '../../../../components/DefinitionsList';
import { getCountryBy2LetterCode } from '../../../../../../utils/countries';
import { ActiveStatus } from '../../../../components/ActiveStatus';
import { useLegalEntitiesStore } from '../store';
import { useMutation } from 'react-query';
import { useDeleteEntityModal } from '../deleteEntityModal';
import { useEditLegalEntitiesDrawer } from '../editLegalEntitiesDrawer';
import classes from './LegalEntity.module.scss';
import { useStore } from '../../../store';

const getAddressString = ({ street, postalCode, city, countryCode }) => {
    const country = getCountryBy2LetterCode(countryCode);
    const partWithoutStreet = [postalCode, city, country].join(' ');
    return [street, partWithoutStreet].join(', ');
};

const ActivateActionItem = ({ id }) => {
    const { activateEntity } = useLegalEntitiesStore();
    const activateMutation = useMutation(() => activateEntity(id));
    const { t } = useTranslation();

    return (
        <Dropdown.Item disabled={activateMutation.isLoading} onClick={() => activateMutation.mutate()}>
            {t('sauron.financeModule.documentHeads.actions.activate')}
        </Dropdown.Item>
    );
};

ActivateActionItem.propTypes = {
    id: PropTypes.string.isRequired
};

const DeactivateActionItem = ({ id }) => {
    const { deactivateEntity } = useLegalEntitiesStore();
    const deactivateMutation = useMutation(() => deactivateEntity(id));
    const { t } = useTranslation();

    return (
        <Dropdown.Item disabled={deactivateMutation.isLoading} onClick={() => deactivateMutation.mutate()}>
            {t('sauron.financeModule.documentHeads.actions.deactivate')}
        </Dropdown.Item>
    );
};

DeactivateActionItem.propTypes = {
    id: PropTypes.string.isRequired
};

export const LegalEntity = ({ data, isOpenByDefault }) => {
    const { t } = useTranslation();
    const { open: openDeleteModal } = useDeleteEntityModal();

    const editDrawer = useEditLegalEntitiesDrawer();
    const store = useStore();

    const renderTitle = () => (
        <div className={classes.header}>
            <div>
                <div className={classes.title}>{data.name}</div>
                <div className={classes.subtitle}>{data.description}</div>
            </div>
            <div className={classes.headerRight}>
                <ActiveStatus isActive={data.is_active} />
                <StopPropagation>
                    <Dropdown>
                        <Dropdown.Item onClick={() => editDrawer.edit(data)}>{t('common.cta.edit')}</Dropdown.Item>
                        <Dropdown.Item disabled={!store.isLegalEntityDeletable(data.id)} onClick={() => openDeleteModal(data.id)}>
                            {t('common.cta.delete')}
                        </Dropdown.Item>
                        {data.is_active ? <DeactivateActionItem id={data.id} /> : <ActivateActionItem id={data.id} />}
                    </Dropdown>
                </StopPropagation>
            </div>
        </div>
    );

    return (
        <div className={classes.root}>
            <Accordion title={renderTitle()} openByDefault={isOpenByDefault}>
                <DefinitionsList
                    list={[
                        {
                            key: 'address',
                            term: t('sauron.financeModule.documentHeads.fields.address'),
                            definition: getAddressString({
                                street: data.address.street_line1,
                                postalCode: data.address.postal_code,
                                city: data.address.city,
                                countryCode: data.address.country
                            })
                        },
                        {
                            key: 'vatCountry',
                            term: t('sauron.financeModule.documentHeads.fields.vatCountry'),
                            definition: getCountryBy2LetterCode(data.vat_country)
                        },
                        {
                            key: 'vatNumber',
                            term: t('sauron.financeModule.documentHeads.fields.vatNumber'),
                            definition: data.vat_number
                        }
                    ]}
                />
            </Accordion>
        </div>
    );
};

LegalEntity.propTypes = {
    data: PropTypes.object,
    isOpenByDefault: PropTypes.bool
};
